import { ChangeEvent, useMemo, useState } from "react";
import { useAppSelector } from "../../../../stores/hooks/hooks.ts";
import { faPen } from "@fortawesome/pro-solid-svg-icons";
import { formatDateToMonthDayYear } from "../../../../helpers/functions/formatDate.tsx";
import {
  BrandItemCompanyDataType,
  CompanyMember,
  CompanySettingsType,
} from "../../../../stores/types/companyTypes.tsx";
import { BrandsView } from "./components/BrandsView.tsx";
import { UsersView } from "./components/UsersView.tsx";
import {
  RevActionsHeader,
  RevButton,
  RevDialog,
  RevIconButton,
  RevInput,
  RevLink,
  RevTabs,
} from "@revmatics/catalyst";
import { useNavigate, useParams } from "react-router";
import { InviteUserDialog } from "./components/InviteUserDialog.tsx";
import { useFlags } from "launchdarkly-react-client-sdk";
import { faArrowLeft } from "@fortawesome/pro-light-svg-icons";

type Props = {
  handleSubmit: () => void;
  companyName: string;
  setCompanyName: (name: string) => void;
  brandList?: BrandItemCompanyDataType[];
  isLoadingBrands?: boolean;
  companyUsers?: CompanyMember[];
  isLoadingUsers?: boolean;
  isOpenEditCompanyName: boolean;
  setIsOpenEditCompanyName: (value: boolean) => void;
  companySettings?: CompanySettingsType;
  isLoadingSettings?: boolean;
  backButton?: boolean;
};

export const CompanySettingsPage = ({
  handleSubmit,
  companyName,
  setCompanyName,
  brandList,
  isLoadingBrands,
  isLoadingUsers,
  companyUsers,
  isOpenEditCompanyName,
  setIsOpenEditCompanyName,
  companySettings,
  isLoadingSettings,
  backButton = false,
}: Props) => {
  const navigate = useNavigate();
  const { inviteUser: ffInviteUser, addBrand: ffAddBrand } = useFlags();
  const { isLoadingCompanyName } = useAppSelector((state) => state.company);
  const [activeTab, setActiveTab] = useState("brands");
  const errorEditCompany = useAppSelector(
    (state) => state.company.errorEditCompany,
  );
  const organizationsList = useAppSelector(
    (state) => state.organizations.organizationsList,
  );

  const [isOpenAddBrand, setIsOpenAddBrand] = useState(false);
  const [isInviteUserOpen, setIsInviteUserOpen] = useState(false);
  const { companyId } = useParams();

  const tabData = [
    { id: "brands", title: "Brands" },
    { id: "users", title: "Users" },
  ];

  const isAdmin = useMemo(() => {
    if (!companySettings) return false;
    return (
      organizationsList?.find((org) => org.id === companySettings.id)?.role ===
      "ORGANIZATION_ADMIN"
    );
  }, [companySettings, organizationsList]);

  return (
    <div className="space-y-6">
      <RevActionsHeader
        headerClassName="relative"
        title={`${companySettings?.name ?? ""}`}
        headerStatusBadge={
          <RevIconButton
            icon={faPen}
            iconSize="small"
            ariaLabel="edit company name"
            handleClick={() => setIsOpenEditCompanyName(true)}
          />
        }
        subtitle={
          <div className="sm:flex justify-between gap-2">
            <div className="flex items-center gap-6 typography-body-sm text-monochrome-900">
              <p>Company ID: {companySettings?.id}</p>
              <p>
                Created on:{" "}
                {formatDateToMonthDayYear(companySettings?.created_at || "")}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 sm:absolute right-0 sm:-translate-y-3/4 flex gap-3">
              {ffAddBrand && isAdmin && (
                <RevLink
                  pathname="brand/create"
                  text="Add Brand"
                  buttonVariant="primary"
                  className="w-fit"
                  displayAs="button"
                  buttonSize="small"
                />
              )}
              {ffAddBrand && !isAdmin && (
                <RevButton
                  text="Add Brand"
                  variant="primary"
                  handleClick={() => setIsOpenAddBrand(true)}
                  className="w-fit"
                  buttonSize="small"
                />
              )}
              {isAdmin && !companyId && ffInviteUser && (
                <RevButton
                  text="Invite User"
                  variant="primary"
                  handleClick={() => setIsInviteUserOpen(true)}
                  className="w-fit"
                  buttonSize="small"
                />
              )}
            </div>
          </div>
        }
        backButton={
          backButton ? (
            <RevIconButton
              handleClick={() => navigate(-1)}
              icon={faArrowLeft}
              size="xl"
              variant="outline"
              iconSize="medium"
              aria-label="Back button brand details"
              className="bg-white"
            />
          ) : undefined
        }
      />
      <RevTabs
        data={tabData}
        selected={activeTab}
        handleSelect={setActiveTab}
        tabId="company-settings-tabs"
        size="medium"
      />

      {activeTab === "brands" && (
        <BrandsView
          brandList={brandList}
          isLoading={isLoadingBrands}
          setIsOpenAddBrand={setIsOpenAddBrand}
        />
      )}
      {activeTab === "users" && (
        <UsersView companyUsers={companyUsers} isLoading={isLoadingUsers} />
      )}

      <RevDialog
        isOpen={isOpenEditCompanyName}
        title="Edit Company Name"
        setIsOpen={() => setIsOpenEditCompanyName(false)}
        data-testid="edit-company-dialog"
        btnList={[
          <RevButton
            text="Cancel"
            variant="secondary"
            handleClick={() => setIsOpenEditCompanyName(false)}
            key="setting-company-cancel-name-edit"
          />,
          <RevButton
            text="Save"
            variant="primary"
            handleClick={handleSubmit}
            isLoading={isLoadingCompanyName || isLoadingSettings}
            key="setting-company-save-name-edit"
          />,
        ]}
      >
        <RevInput
          name="companyName"
          type="text"
          label="Company Name"
          size="md"
          data-testid="input-edit-company-name"
          value={companyName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setCompanyName(e.target.value)
          }
          error={errorEditCompany || undefined}
        />
      </RevDialog>
      <InviteUserDialog
        isOpen={isInviteUserOpen}
        setIsOpen={setIsInviteUserOpen}
      />
      <RevDialog
        isOpen={isOpenAddBrand}
        variant="info"
        title="Add Brand"
        setIsOpen={() => setIsOpenAddBrand(false)}
        btnList={[
          <RevLink
            pathname="/contact-us"
            text="Contact Us"
            buttonVariant="primary"
            displayAs="button"
            buttonSize="medium"
            key="setting-company-contact-us-add-brand"
          />,
          <RevButton
            text="I'm done"
            variant="secondary"
            handleClick={() => setIsOpenAddBrand(false)}
            key="setting-company-done-add-brand"
          />,
        ]}
      >
        <div className="typography-body-sm text-monochrome-900 text-center">
          In order to add another brand, please contact{" "}
          <a
            href="mailto:support@revmatics.ai"
            className="underline hover:no-underline"
          >
            support@revmatics.ai
          </a>{" "}
          so that a Revmatics specialist can help you.
        </div>
      </RevDialog>
    </div>
  );
};
